import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Twitter, Linkedin, Instagram, Youtube, ArrowUpRight } from 'lucide-react';
import { mockCompanyInfo } from '../mock';
import { CookieSettingsButton } from './CookieConsent';
import DarkPattern from './DarkPattern';

const socials = [
  { icon: Twitter, href: mockCompanyInfo.social.twitter, label: 'Twitter' },
  { icon: Linkedin, href: mockCompanyInfo.social.linkedin, label: 'LinkedIn' },
  { icon: Instagram, href: mockCompanyInfo.social.instagram, label: 'Instagram' },
  { icon: Youtube, href: mockCompanyInfo.social.youtube, label: 'YouTube' },
];

const serviceLinks = [
  { label: 'PODs', to: '/services/offshore-pods' },
  { label: 'Cloud Services', to: '/services/cloud-managed' },
  { label: 'GCC Consulting', to: '/services/bot-consulting' },
];

const companyLinks = [
  { label: 'About Us', to: '/about' },
  { label: 'Blog', to: '/blog' },
  { label: 'Case Studies', to: '/case-studies' },
  { label: 'Partnerships', to: '/partnerships' },
  { label: 'FAQ', to: '/faq' },
];

const Footer = () => {
  return (
    <footer className="relative overflow-hidden bg-gray-900 text-gray-400" data-testid="site-footer">
      <DarkPattern variant="mesh" glow="bl" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 py-14">
          {/* Brand */}
          <div className="lg:col-span-4">
            <img src="/niva-footer-logo.jpg" alt="NIVA Digital" className="h-12 mb-5" />
            <p className="text-sm leading-relaxed max-w-xs mb-6">
              We help you to adopt AI.
            </p>
            <div className="flex items-center gap-3">
              {socials.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-800 text-gray-400 hover:bg-red-600 hover:text-white transition-colors duration-200"
                >
                  <Icon size={17} />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h3 className="text-white font-semibold text-sm tracking-wide uppercase mb-4">Services</h3>
            <ul className="space-y-3 text-sm">
              {serviceLinks.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="hover:text-red-500 transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="lg:col-span-2">
            <h3 className="text-white font-semibold text-sm tracking-wide uppercase mb-4">Company</h3>
            <ul className="space-y-3 text-sm">
              {companyLinks.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="hover:text-red-500 transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="https://nivadigital.zohorecruit.in/jobs/Careers"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:text-red-500 transition-colors"
                  data-testid="footer-link-careers"
                >
                  Careers <ArrowUpRight size={13} />
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <h3 className="text-white font-semibold text-sm tracking-wide uppercase mb-4">Get in Touch</h3>
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3">
                <Mail size={16} className="text-red-500 flex-shrink-0" />
                <a href={`mailto:${mockCompanyInfo.email}`} className="hover:text-red-500 transition-colors">
                  {mockCompanyInfo.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={16} className="text-red-500 flex-shrink-0" />
                <a href={`tel:${mockCompanyInfo.phone}`} className="hover:text-red-500 transition-colors">
                  {mockCompanyInfo.phone}
                </a>
              </li>
              {mockCompanyInfo.locations.map((loc) => (
                <li key={loc.country} className="flex items-start gap-3">
                  <MapPin size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <span>
                    <span className="block text-gray-300 font-medium">{loc.country}</span>
                    <span className="block text-gray-500 leading-relaxed">{loc.address}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <p>&copy; {new Date().getFullYear()} NIVA Digital, LLC. All rights reserved.</p>
            <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2">
              <Link to="/privacy-policy" className="hover:text-red-500 transition-colors">Privacy Policy</Link>
              <Link to="/privacy-policy" className="hover:text-red-500 transition-colors">Terms of Service</Link>
              <CookieSettingsButton />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
